// File: src/pages/reports/GSTR2B_B2B.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Input,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
} from "reactstrap";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { ImFileExcel, ImFilePdf } from "react-icons/im";
import { FiFilter } from "react-icons/fi";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";

// ===== your modules (all via ../import/*) =====
import { PurchaseOrderList } from "../import/ApiEndPoint/ApiCalling";
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";

// ---------- inline CSS ----------
const injectOnce = () => {
  if (document.getElementById("gstr2b-inline-css")) return;
  const css = `
    .g2HdrCard{
      border:1px solid #e5e7eb;
      background:#ffffff;
      border-radius:16px;
      padding:10px 12px;
      box-shadow: 0 6px 18px rgba(15,23,42,.05);
    }

    .g2HdrRow{
      display:flex;
      align-items:flex-end;
      gap:10px;
      flex-wrap:nowrap;
      overflow-x:auto;
      -webkit-overflow-scrolling: touch;
      padding-bottom:2px;
    }
    .g2HdrRow::-webkit-scrollbar{height:6px}
    .g2HdrRow::-webkit-scrollbar-thumb{background:#e5e7eb;border-radius:999px}

    .g2Field{
      display:flex;
      flex-direction:column;
      gap:4px;
      min-width:170px;
    }
    .g2Field.small{min-width:140px}
    .g2Field.wide{min-width:220px}
    .g2Lbl{
      font-size:11px;
      color:#64748b;
      font-weight:800;
      letter-spacing:.02em;
      line-height:1;
      margin:0;
      text-transform:uppercase;
      white-space:nowrap;
    }
    .g2Ctl{
      height:34px !important;
      border-radius:12px !important;
      border:1px solid #e5e7eb !important;
      padding:6px 10px !important;
      background:#fff;
    }

    .g2IconBtn{
      height:34px;
      width:42px;
      border-radius:12px;
      display:inline-flex;
      align-items:center;
      justify-content:center;
      padding:0;
    }
    .g2IconBtn svg{font-size:18px}

    .g2Btn{
      height:34px;
      border-radius:12px;
      padding:0 12px;
      display:inline-flex;
      align-items:center;
      justify-content:center;
      white-space:nowrap;
    }

    .g2Spacer{flex:1; min-width:16px}

    .g2Search{
      height:34px !important;
      border-radius:12px !important;
      border:1px solid #e5e7eb !important;
      padding:6px 10px !important;
      background:#fff;
      width:280px;
      min-width:220px;
    }

    .list-card{border:1px solid #e5e7eb;border-radius:14px;padding:8px}
    .wl-table-wrap{width:100%;overflow:auto;border:1px solid #e5e7eb;border-radius:14px;background:#fff}
    .wl-table{width:100%;border-collapse:collapse;min-width:1500px}
    .wl-table thead th{
      background:#f4f7f6;color:#0f172a;font-weight:700;text-transform:uppercase;
      font-size:12px;letter-spacing:.03em;border-bottom:1px solid #e5e7eb;
      padding:6px 8px;white-space:nowrap;text-align:center;position:sticky;top:0;z-index:1
    }
    .wl-table tbody td{
      padding:6px 8px;border-bottom:1px solid #f1f5f9;color:#0f172a;
      font-size:14px;text-align:center;vertical-align:middle
    }
    .wl-table tbody tr:hover{background:#f9fbff}

    .g2MonthRow td{
      background:#eef6ff;
      font-weight:900;
      cursor:pointer;
    }
    .g2MonthRow:hover td{ background:#e4efff; }
    .g2MonthName{
      display:flex;
      align-items:center;
      gap:8px;
      justify-content:flex-start;
    }
    .g2FinalRow td{
      background:#f8fafc;
      font-weight:900;
    }
    .g2Muted{color:#64748b}
  `;
  const s = document.createElement("style");
  s.id = "gstr2b-inline-css";
  s.innerHTML = css;
  document.head.appendChild(s);
};

const num = (n) => Number(n || 0).toFixed(2);
const tdRight = { textAlign: "right" };

const isCompletedStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase() === "completed";

const buildSelectedDatabase = (db) => {
  const financialYearSelected = localStorage.getItem("financialYearSelected");

  if (!db) return "";

  return financialYearSelected && !/-\d{4}-\d{2}$/.test(String(db))
    ? `${db}-${financialYearSelected}`
    : db;
};

const safeDate = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toISOString().split("T")[0];
};

const hasOwn = (obj, key) =>
  Object.prototype.hasOwnProperty.call(obj || {}, key);

const firstFilled = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      return String(v).trim();
    }
  }
  return "";
};

const getPlaceOfSupply = (r) =>
  firstFilled(
    r?.placeOfSupply,
    r?.state,
    r?.State,
    r?.partyId?.state,
    r?.partyId?.State,
    r?.partyId?.stateName,
    r?.partyId?.billingState,
    r?.partyId?.shippingState,
    r?.partyId?.billingAddress?.state,
    r?.partyId?.shippingAddress?.state,
    r?.billingAddress?.state,
    r?.shippingAddress?.state,
    r?.deliveryAddress?.state,
    r?.consigneeAddress?.state,
  ) || "-";

const getReverseCharge = (r) => {
  if (!r || typeof r !== "object") return "N";
  if (!hasOwn(r, "transportationCost")) return "N";
  const val = r?.transportationCost;
  if (val === undefined || val === null) return "N";
  if (typeof val === "string" && val.trim() === "") return "N";
  return "Y";
};

const getGstRateList = (r) => {
  const items = Array.isArray(r?.orderItems) ? r.orderItems : [];
  const out = items
    .map((item) => {
      const raw =
        item?.gstPercentage ??
        item?.gstpercentage ??
        item?.gstRate ??
        item?.gstrate ??
        item?.tax ??
        item?.gst;
      if (raw === undefined || raw === null || String(raw).trim() === "") {
        return null;
      }
      const clean = String(raw).replace(/%/g, "").trim();
      return clean ? `${clean}%` : null;
    })
    .filter(Boolean);
  return out.length ? out.join(", ") : "-";
};

const buildSearchBucket = (r) =>
  [
    r?.partyId?.gstNumber,
    r?.partyId?.CompanyName,
    r?.invoiceId,
    safeDate(r?.date),
    num(r?.grandTotal),
    num(r?.amount),
    getPlaceOfSupply(r),
    getReverseCharge(r),
    getGstRateList(r),
    num(r?.igstTotal),
    num(r?.cgstTotal),
    num(r?.sgstTotal),
  ]
    .join(" | ")
    .toLowerCase();

// FY / Period helpers
const MONTHS_FY = [
  { key: 4, label: "April" },
  { key: 5, label: "May" },
  { key: 6, label: "June" },
  { key: 7, label: "July" },
  { key: 8, label: "August" },
  { key: 9, label: "September" },
  { key: 10, label: "October" },
  { key: 11, label: "November" },
  { key: 12, label: "December" },
  { key: 1, label: "January" },
  { key: 2, label: "February" },
  { key: 3, label: "March" },
];

const QUARTERS = [
  { value: "Q1", label: "Q1 (Apr - Jun)", months: [4, 5, 6] },
  { value: "Q2", label: "Q2 (Jul - Sep)", months: [7, 8, 9] },
  { value: "Q3", label: "Q3 (Oct - Dec)", months: [10, 11, 12] },
  { value: "Q4", label: "Q4 (Jan - Mar)", months: [1, 2, 3] },
];

const HALVES = [
  { value: "H1", label: "H1 (Apr - Sep)", months: [4, 5, 6, 7, 8, 9] },
  { value: "H2", label: "H2 (Oct - Mar)", months: [10, 11, 12, 1, 2, 3] },
];

function getFinancialYearString(dateObj) {
  const d = new Date(dateObj);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  return m >= 4 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
}

function getFinancialYearStartYear(fy) {
  const n = Number(String(fy).split("-")[0]);
  return Number.isFinite(n) ? n : new Date().getFullYear();
}

function getFYMonthMeta(dateObj) {
  const d = new Date(dateObj);
  const month = d.getMonth() + 1;
  const year = d.getFullYear();
  const fy = getFinancialYearString(d);

  const labelMap = {
    4: "April",
    5: "May",
    6: "June",
    7: "July",
    8: "August",
    9: "September",
    10: "October",
    11: "November",
    12: "December",
    1: "January",
    2: "February",
    3: "March",
  };

  const sortIndexMap = {
    4: 1,
    5: 2,
    6: 3,
    7: 4,
    8: 5,
    9: 6,
    10: 7,
    11: 8,
    12: 9,
    1: 10,
    2: 11,
    3: 12,
  };

  const fyStart = getFinancialYearStartYear(fy);
  const displayYear = month >= 4 ? fyStart : fyStart + 1;

  return {
    fy,
    month,
    label: `${labelMap[month]} ${displayYear}`,
    sortIndex: sortIndexMap[month],
    displayYear,
    realYear: year,
  };
}

const makeTotals = (rows = []) => ({
  invVal: rows.reduce((acc, r) => acc + Number(r?.grandTotal || 0), 0),
  taxable: rows.reduce((acc, r) => acc + Number(r?.amount || 0), 0),
  igst: rows.reduce((acc, r) => acc + Number(r?.igstTotal || 0), 0),
  cgst: rows.reduce((acc, r) => acc + Number(r?.cgstTotal || 0), 0),
  sgst: rows.reduce((acc, r) => acc + Number(r?.sgstTotal || 0), 0),
});

function groupRowsByMonth(rows = []) {
  const bucket = {};
  rows.forEach((r) => {
    const dt = r?.date || r?.createdAt || r?.updatedAt;
    if (!dt) return;
    const meta = getFYMonthMeta(new Date(dt));
    const key = `${meta.fy}-${meta.month}`;
    if (!bucket[key]) {
      bucket[key] = {
        key,
        label: meta.label,
        month: meta.month,
        fy: meta.fy,
        sortIndex: meta.sortIndex,
        rows: [],
      };
    }
    bucket[key].rows.push(r);
  });

  return Object.values(bucket)
    .map((g) => ({
      ...g,
      rows: g.rows.sort(
        (a, b) =>
          new Date(b?.date || b?.createdAt || 0) -
          new Date(a?.date || a?.createdAt || 0),
      ),
      totals: makeTotals(g.rows),
    }))
    .sort((a, b) => a.sortIndex - b.sortIndex);
}

const DEFAULT_COLS = [
  { key: "sr", label: "Sr. No", width: 80, render: (_r, i) => i + 1 },
  {
    key: "gstin",
    label: "Supplier GSTIN",
    width: 180,
    render: (r) => r?.partyId?.gstNumber || "-",
  },
  {
    key: "name",
    label: "Supplier Name",
    width: 220,
    render: (r) => r?.partyId?.CompanyName || "-",
  },
  {
    key: "invNo",
    label: "Invoice No",
    width: 160,
    render: (r) => r?.invoiceId || "-",
  },
  {
    key: "invDate",
    label: "Invoice Date",
    width: 140,
    render: (r) => safeDate(r?.date),
  },
  {
    key: "invVal",
    label: "Invoice Value",
    width: 140,
    alignRight: true,
    render: (r) => num(r?.grandTotal),
  },
  {
    key: "taxable",
    label: "Taxable Value",
    width: 140,
    alignRight: true,
    render: (r) => num(r?.amount),
  },
  {
    key: "pos",
    label: "Place of Supply",
    width: 170,
    render: (r) => getPlaceOfSupply(r),
  },
  {
    key: "rcm",
    label: "Reverse Charge (Y/N)",
    width: 170,
    render: (r) => getReverseCharge(r),
  },
  {
    key: "rate",
    label: "GST Rate",
    width: 220,
    render: (r) => getGstRateList(r),
  },
  {
    key: "igst",
    label: "IGST Amount",
    width: 130,
    alignRight: true,
    render: (r) => num(r?.igstTotal),
  },
  {
    key: "cgst",
    label: "CGST Amount",
    width: 130,
    alignRight: true,
    render: (r) => num(r?.cgstTotal),
  },
  {
    key: "sgst",
    label: "SGST Amount",
    width: 130,
    alignRight: true,
    render: (r) => num(r?.sgstTotal),
  },
];

export default function GSTR2B_B2B() {
  const [loading, setLoading] = useState(true);
  const [masterShow, setMasterShow] = useState(false);
  const [perm, setPerm] = useState({ View: true, Download: true });

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [search, setSearch] = useState("");

  // FY / period filters
  const [fy, setFy] = useState("");
  const [periodType, setPeriodType] = useState("YEARLY");
  const [selectedMonth, setSelectedMonth] = useState(4);
  const [selectedQuarter, setSelectedQuarter] = useState("Q1");
  const [selectedHalf, setSelectedHalf] = useState("H1");
  const [monthExpanded, setMonthExpanded] = useState({});

  const [chooserOpen, setChooserOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const trayRef = useRef(null);

  const getSavedCols = () => {
    const saved = localStorage.getItem("GSTR2BList");
    const mapByKey = new Map(DEFAULT_COLS.map((c) => [c.key, c]));
    const mapByLabel = new Map(DEFAULT_COLS.map((c) => [c.label, c]));

    if (!saved) return DEFAULT_COLS;

    try {
      const parsed = JSON.parse(saved);

      if (Array.isArray(parsed)) {
        if (typeof parsed[0] === "string") {
          const next = parsed.map((k) => mapByKey.get(k)).filter(Boolean);
          return next.length ? next : DEFAULT_COLS;
        }

        if (parsed[0]?.headerName) {
          const next = parsed
            .map((d) => mapByLabel.get(d.headerName))
            .filter(Boolean);
          return next.length ? next : DEFAULT_COLS;
        }

        if (parsed[0]?.key) {
          const next = parsed.map((d) => mapByKey.get(d.key)).filter(Boolean);
          return next.length ? next : DEFAULT_COLS;
        }
      }
    } catch {}

    return DEFAULT_COLS;
  };

  const [cols, setCols] = useState(getSavedCols);

  const availableCols = useMemo(
    () => DEFAULT_COLS.filter((c) => !cols.find((k) => k.key === c.key)),
    [cols],
  );

  const persistCols = (newCols) => {
    setCols(newCols);
    localStorage.setItem(
      "GSTR2BList",
      JSON.stringify(newCols.map((c) => c.key)),
    );
  };

  const loadInvoices = async (uid, db) => {
    try {
      setLoading(true);

      const selectedDatabase = buildSelectedDatabase(db);
      const res = await PurchaseOrderList(uid, selectedDatabase);

      const list = Array.isArray(res?.orderHistory) ? res.orderHistory : [];
      const final = list
        .filter((r) => isCompletedStatus(r?.status))
        .slice()
        .reverse();

      setRows(final);
      setAllRows(final);
    } catch (err) {
      console.error("[GSTR2B_B2B] fetch error:", err);
      setRows([]);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    injectOnce();
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    if (user?.rolename?.roleName === "MASTER") setMasterShow(true);
    setPerm(CheckPermission("GSTR 2B") || { View: true, Download: true });
    loadInvoices(user?._id, user?.database);
  }, []);

  const fyOptions = useMemo(() => {
    const fySet = [
      ...new Set(
        allRows
          .map((r) => r?.date || r?.createdAt || r?.updatedAt)
          .filter(Boolean)
          .map((d) => getFinancialYearString(new Date(d))),
      ),
    ].sort((a, b) => Number(b.split("-")[0]) - Number(a.split("-")[0]));
    return fySet;
  }, [allRows]);

  useEffect(() => {
    if (!fyOptions.length) return;
    if (!fy || !fyOptions.includes(fy)) {
      setFy(fyOptions[0]);
    }
  }, [fyOptions, fy]);

  const baseFilteredRows = useMemo(() => {
    const source = rows || [];

    return source.filter((r) => {
      const iso = safeDate(r?.date || r?.createdAt || r?.updatedAt);

      if (appliedStartDate && iso !== "-" && iso < appliedStartDate)
        return false;
      if (appliedEndDate && iso !== "-" && iso > appliedEndDate) return false;

      if (fy) {
        const dt = r?.date || r?.createdAt || r?.updatedAt;
        if (!dt) return false;
        if (getFinancialYearString(new Date(dt)) !== fy) return false;
      }

      if (periodType === "MONTHLY") {
        const dt = r?.date || r?.createdAt || r?.updatedAt;
        if (!dt) return false;
        if (getFYMonthMeta(new Date(dt)).month !== Number(selectedMonth)) {
          return false;
        }
      } else if (periodType === "QUARTERLY") {
        const q = QUARTERS.find((x) => x.value === selectedQuarter);
        const months = q?.months || [];
        const dt = r?.date || r?.createdAt || r?.updatedAt;
        if (!dt || !months.includes(getFYMonthMeta(new Date(dt)).month)) {
          return false;
        }
      } else if (periodType === "HALF_YEARLY") {
        const h = HALVES.find((x) => x.value === selectedHalf);
        const months = h?.months || [];
        const dt = r?.date || r?.createdAt || r?.updatedAt;
        if (!dt || !months.includes(getFYMonthMeta(new Date(dt)).month)) {
          return false;
        }
      }

      return true;
    });
  }, [
    rows,
    appliedStartDate,
    appliedEndDate,
    fy,
    periodType,
    selectedMonth,
    selectedQuarter,
    selectedHalf,
  ]);

  const filtered = useMemo(() => {
    const term = (search || "").toLowerCase().trim();
    const source = baseFilteredRows || [];

    return source.filter((r) => {
      if (!term) return true;
      return buildSearchBucket(r).includes(term);
    });
  }, [baseFilteredRows, search]);

  const groupedRows = useMemo(() => groupRowsByMonth(filtered), [filtered]);

  useEffect(() => {
    const initial = {};
    groupedRows.forEach((g) => {
      initial[g.key] = false;
    });
    setMonthExpanded(initial);
  }, [groupedRows]);

  const toggleMonth = (key) => {
    setMonthExpanded((prev) => {
      const isCurrentlyOpen = !!prev[key];
      const next = {};
      Object.keys(prev).forEach((k) => {
        next[k] = false;
      });
      next[key] = !isCurrentlyOpen;
      return next;
    });
  };

  const handleSubmitDate = () => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
  };

  const addCols = (toAddKeys) => {
    const map = new Map(DEFAULT_COLS.map((c) => [c.key, c]));
    const added = toAddKeys.map((k) => map.get(k)).filter(Boolean);
    const next = [...cols, ...added];
    persistCols(next);
  };

  const removeAt = (idx) => {
    const clone = [...cols];
    clone.splice(idx, 1);
    persistCols(clone);
    setSelectedIndex(-1);
  };

  const moveUp = () => {
    if (selectedIndex <= 0) return;
    const clone = [...cols];
    const [it] = clone.splice(selectedIndex, 1);
    clone.splice(selectedIndex - 1, 0, it);
    setSelectedIndex(selectedIndex - 1);
    persistCols(clone);
  };

  const moveDown = () => {
    if (selectedIndex < 0 || selectedIndex >= cols.length - 1) return;
    const clone = [...cols];
    const [it] = clone.splice(selectedIndex, 1);
    clone.splice(selectedIndex + 1, 0, it);
    setSelectedIndex(selectedIndex + 1);
    persistCols(clone);
  };

  const asAoa = (rowsForExport) => {
    const head = cols.map((c) => c.label);
    const body = rowsForExport.map((r, i) =>
      cols.map((c) => {
        const val = c.key === "sr" ? i + 1 : c.render(r, i);
        return typeof val === "string" ? val : String(val ?? "");
      }),
    );
    return [head, ...body];
  };

  const downloadCSV = () => {
    const aoa = asAoa(filtered);
    const csv = aoa
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "GSTR2B_B2B.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadXLS = () => {
    const ws = XLSX.utils.aoa_to_sheet(asAoa(filtered));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "GSTR2B");
    XLSX.writeFile(wb, "GSTR2B_B2B.xlsx");
  };

  const downloadPDF = () => {
    const doc = new jsPDF("landscape");
    doc.setFontSize(14);
    doc.text("GSTR 2B - (B2B)", 14, 16);

    const head = [cols.map((c) => c.label)];
    const body = filtered.map((r, i) =>
      cols.map((c) => (c.key === "sr" ? i + 1 : c.render(r, i))),
    );

    doc.autoTable({
      head,
      body,
      startY: 22,
      styles: {
        fontSize: 9,
        cellPadding: 2,
        halign: "center",
        valign: "middle",
      },
      headStyles: { fillColor: [244, 247, 246], textColor: [0, 0, 0] },
      alternateRowStyles: { fillColor: [248, 250, 255] },
    });

    doc.save("GSTR2B_B2B.pdf");
  };

  const grandTotals = useMemo(() => makeTotals(filtered), [filtered]);

  const renderGroupCell = (c, group) => {
    switch (c.key) {
      case "sr":
        return (
          <div className="g2MonthName">
            {monthExpanded[group.key] ? <FaChevronDown /> : <FaChevronRight />}
            <span>{group.label}</span>
          </div>
        );
      case "invVal":
        return num(group.totals.invVal);
      case "taxable":
        return num(group.totals.taxable);
      case "igst":
        return num(group.totals.igst);
      case "cgst":
        return num(group.totals.cgst);
      case "sgst":
        return num(group.totals.sgst);
      case "name":
        return "Month Total";
      default:
        return "—";
    }
  };

  const renderFinalCell = (c) => {
    switch (c.key) {
      case "sr":
        return "Final Total";
      case "invVal":
        return num(grandTotals.invVal);
      case "taxable":
        return num(grandTotals.taxable);
      case "igst":
        return num(grandTotals.igst);
      case "cgst":
        return num(grandTotals.cgst);
      case "sgst":
        return num(grandTotals.sgst);
      default:
        return "—";
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading…
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <Row className="mt-1">
        <Col sm="12">
          <Card className="g2HdrCard">
            <div className="g2HdrRow" ref={trayRef}>
              {masterShow ? (
                <div style={{ minWidth: 240 }}>
                  <SuperAdminUI
                    onDropdownChange={(val) =>
                      localStorage.setItem(
                        "SuperadminIdByMaster",
                        JSON.stringify(val),
                      )
                    }
                    onSubmit={(e) => {
                      e.preventDefault();
                      const mix = JSON.parse(
                        localStorage.getItem("SuperadminIdByMaster") || '""',
                      );
                      if (!mix) return;
                      const [id, db] = String(mix).split(" ");
                      loadInvoices(id, db);
                    }}
                  />
                </div>
              ) : null}

              {/* <div className="g2Field">
                <div className="g2Lbl">Financial Year</div>
                <Input
                  type="select"
                  className="g2Ctl"
                  value={fy}
                  onChange={(e) => setFy(e.target.value)}
                >
                  {fyOptions.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </Input>
              </div> */}

              <div className="g2Field">
                <div className="g2Lbl">Filter Type</div>
                <Input
                  type="select"
                  className="g2Ctl"
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                >
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly</option>
                  <option value="HALF_YEARLY">Half Yearly</option>
                  <option value="YEARLY">Yearly</option>
                </Input>
              </div>

              {periodType === "MONTHLY" && (
                <div className="g2Field">
                  <div className="g2Lbl">Month</div>
                  <Input
                    type="select"
                    className="g2Ctl"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  >
                    {MONTHS_FY.map((m) => (
                      <option key={m.key} value={m.key}>
                        {m.label}
                      </option>
                    ))}
                  </Input>
                </div>
              )}

              {periodType === "QUARTERLY" && (
                <div className="g2Field">
                  <div className="g2Lbl">Quarter</div>
                  <Input
                    type="select"
                    className="g2Ctl"
                    value={selectedQuarter}
                    onChange={(e) => setSelectedQuarter(e.target.value)}
                  >
                    {QUARTERS.map((q) => (
                      <option key={q.value} value={q.value}>
                        {q.label}
                      </option>
                    ))}
                  </Input>
                </div>
              )}

              {periodType === "HALF_YEARLY" && (
                <div className="g2Field">
                  <div className="g2Lbl">Half Year</div>
                  <Input
                    type="select"
                    className="g2Ctl"
                    value={selectedHalf}
                    onChange={(e) => setSelectedHalf(e.target.value)}
                  >
                    {HALVES.map((h) => (
                      <option key={h.value} value={h.value}>
                        {h.label}
                      </option>
                    ))}
                  </Input>
                </div>
              )}

              <div className="g2Field wide">
                <div className="g2Lbl">Search</div>
                <Input
                  className="g2Search"
                  placeholder="Search…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="g2Field small">
                <div className="g2Lbl">Start</div>
                <Input
                  type="date"
                  className="g2Ctl"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="g2Field small">
                <div className="g2Lbl">End</div>
                <Input
                  type="date"
                  className="g2Ctl"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div style={{ paddingBottom: 0 }}>
                <Button
                  color="primary"
                  className="g2Btn"
                  onClick={handleSubmitDate}
                >
                  Submit
                </Button>
              </div>

              {perm?.View && (
                <div style={{ paddingBottom: 0 }}>
                  <Button
                    outline
                    color="primary"
                    className="g2IconBtn"
                    onClick={() => setChooserOpen(true)}
                    title="Filter Columns"
                  >
                    <FiFilter />
                  </Button>
                </div>
              )}

              {perm?.Download && (
                <>
                  <div style={{ paddingBottom: 0 }}>
                    <Button
                      outline
                      color="primary"
                      className="g2IconBtn"
                      onClick={downloadPDF}
                      title="Download PDF"
                    >
                      <ImFilePdf />
                    </Button>
                  </div>

                  <div style={{ paddingBottom: 0 }}>
                    <Button
                      outline
                      color="primary"
                      className="g2IconBtn"
                      onClick={downloadXLS}
                      title="Download Excel"
                    >
                      <ImFileExcel />
                    </Button>
                  </div>

                  <div style={{ paddingBottom: 0 }}>
                    <Button
                      outline
                      color="primary"
                      className="g2Btn"
                      onClick={downloadCSV}
                      title="Download CSV"
                    >
                      CSV
                    </Button>
                  </div>
                </>
              )}

              <div className="g2Spacer" />

              <div
                style={{
                  paddingBottom: 6,
                  fontWeight: 900,
                  color: "#0f172a",
                  whiteSpace: "nowrap",
                }}
              >
                {filtered.length}
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="mt-1">
        <Col sm="12">
          <Card className="list-card">
            <CardBody style={{ padding: 8 }}>
              <div className="wl-table-wrap">
                <table className="wl-table">
                  <thead>
                    <tr>
                      {cols.map((c) => (
                        <th
                          key={c.key}
                          style={{
                            minWidth: c.width,
                            textAlign: c.alignRight ? "right" : "center",
                          }}
                        >
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {groupedRows.map((group) => (
                      <React.Fragment key={group.key}>
                        <tr
                          className="g2MonthRow"
                          onClick={() => toggleMonth(group.key)}
                        >
                          {cols.map((c) => (
                            <td
                              key={`${group.key}-${c.key}`}
                              style={c.alignRight ? tdRight : undefined}
                            >
                              {renderGroupCell(c, group)}
                            </td>
                          ))}
                        </tr>

                        {monthExpanded[group.key] &&
                          group.rows.map((r, i) => (
                            <tr key={r?._id || `${r?.invoiceId || "row"}-${i}`}>
                              {cols.map((c) => (
                                <td
                                  key={c.key}
                                  style={c.alignRight ? tdRight : undefined}
                                >
                                  {c.key === "sr" ? (
                                    <span className="g2Muted">#{i + 1}</span>
                                  ) : (
                                    c.render(r, i)
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}

                    {filtered.length === 0 && (
                      <tr>
                        <td
                          colSpan={cols.length}
                          style={{
                            padding: 16,
                            textAlign: "center",
                            color: "#64748b",
                          }}
                        >
                          No records
                        </td>
                      </tr>
                    )}

                    {!!filtered.length && (
                      <tr className="g2FinalRow">
                        {cols.map((c) => (
                          <td
                            key={`final-${c.key}`}
                            style={c.alignRight ? tdRight : undefined}
                          >
                            {renderFinalCell(c)}
                          </td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal
        isOpen={chooserOpen}
        toggle={() => setChooserOpen(false)}
        style={{ maxWidth: 900 }}
      >
        <ModalHeader toggle={() => setChooserOpen(false)}>
          Change Fields
        </ModalHeader>

        <ModalBody>
          <Row>
            <Col lg="5" md="5" sm="12">
              <h5>Available Columns</h5>
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 8,
                  maxHeight: 280,
                  overflow: "auto",
                }}
              >
                {availableCols.map((c) => (
                  <label
                    key={c.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 4px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      onChange={(e) =>
                        e.target.checked ? addCols([c.key]) : null
                      }
                    />
                    {c.label}
                  </label>
                ))}

                {availableCols.length === 0 && (
                  <div style={{ color: "#64748b" }}>
                    All columns are visible.
                  </div>
                )}
              </div>
            </Col>

            <Col lg="7" md="7" sm="12">
              <Row>
                <Col md="8">
                  <h5>Visible Columns</h5>
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 10,
                      padding: 8,
                      maxHeight: 280,
                      overflow: "auto",
                    }}
                  >
                    {cols.map((c, idx) => (
                      <div
                        key={c.key}
                        onClick={() => setSelectedIndex(idx)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "6px 8px",
                          marginBottom: 6,
                          borderRadius: 8,
                          background:
                            selectedIndex === idx ? "#e0ecff" : "#fff",
                          border: "1px solid #e5e7eb",
                          cursor: "pointer",
                        }}
                      >
                        <div>{c.label}</div>
                        <Badge
                          color="danger"
                          style={{ cursor: "pointer" }}
                          onClick={() => removeAt(idx)}
                        >
                          Remove
                        </Badge>
                      </div>
                    ))}
                  </div>
                </Col>

                <Col
                  md="4"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Button color="primary" onClick={moveUp} block>
                    Move Up
                  </Button>

                  <Button color="primary" onClick={moveDown} block>
                    Move Down
                  </Button>

                  <Button
                    color="secondary"
                    onClick={() => {
                      persistCols(DEFAULT_COLS);
                      setSelectedIndex(-1);
                    }}
                    block
                  >
                    Reset
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col>
              <div className="d-flex justify-content-center">
                <Badge
                  color="primary"
                  style={{ cursor: "pointer" }}
                  onClick={() => setChooserOpen(false)}
                >
                  Submit
                </Badge>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}
